import React, { useState, useEffect, useCallback, useMemo } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { CaseCourtBindingProvenance } from '@lds-efsp/shared-contracts';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { ChoiceGroup } from '../../../shared/components/ui/ChoiceGroup';
import { dedupePartyRoster } from '../lib/dedupe-party-roster';

const API_BASE = API_BASE_URL;

interface CaseSummary {
  trackingId: string;
  docketId: string;
  title: string;
  category: string;
}

interface CaseDetail {
  trackingId: string;
  docketId: string;
  title: string;
  /** JTI CaseCategoryText — taxonomy inversion: this is actually the CASE_TYPE code */
  category: string;
  /** JTI CaseTypeText — taxonomy inversion: this is actually the CASE_CATEGORY code */
  caseTypeText: string;
  /**
   * The court the CASE ITSELF belongs to (SOR-182) — the CaseCourtBinding the
   * attach persists. Absent when the case record carried no court
   * identification. See `CaseDetail.courtCode` in the api-gateway efm-client.
   */
  courtCode?: string;
  /**
   * How the server attributed `courtCode` (SOR-192) — `case-record` when the
   * case record named its own court, `served-court` when it returned a nil
   * `CaseCourt` and the JTI endpoint that answered supplied the identity
   * (Madera does this on every GetCase). Absent when neither source applied, so
   * the value is unattributed and classifies `absent`.
   *
   * Carried through to `caseInfo.caseSourceCountyProvenance` verbatim by
   * `CaseAttachPanel.handleCaseSelected` — the client attributes nothing of its
   * own, because the only attribution it could synthesize is the `?county=` the
   * lookup ran under, which is exactly the echo SOR-191 exists to distinguish.
   */
  courtCodeProvenance?: CaseCourtBindingProvenance;
  parties: Array<{
    id: string;
    referenceId?: string;
    firstName?: string;
    lastName?: string;
    organizationName?: string;
    role: string;
    isOrganization: boolean;
    /** Court-record contact (WOL-448 / R97) — see CaseRosterParty. */
    mailingAddress?: {
      address1?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
    };
    email?: string;
  }>;
  subcases: Array<{
    id: string;
    type: string;
    title: string;
  }>;
  attorneys: Array<{
    id: string;
    barNumber?: string;
    firstName?: string;
    lastName?: string;
    firmName?: string;
    partyId?: string;
  }>;
}

interface CaseSearchProps {
  brand: BrandConfig;
  county: string;
  onCaseSelected: (caseDetail: CaseDetail) => void;
  /** id for the root container so blocker jump links can scroll to the search. */
  anchorId?: string;
  /**
   * R105 (Walter): confirm-flow-only accelerator that pre-fills the case-number
   * input with the extraction-stashed `trialCourtCaseNumber` the moment the
   * filer lands on the panel. CaseAttachPanel passes it only when
   * `confirmFlowEnabled && !caseTrackingId` (classic surface / attached case →
   * undefined). It writes component-local `searchQuery` ONCE — the initializer
   * seeds it at mount, and the re-seed effect covers a stash that lands after
   * mount — and never over a value the filer has typed (the `!searchQuery`
   * guard). The input keeps binding to `searchQuery`, never to this prop, so the
   * field stays a normal editable input; the filer still runs the search and
   * selects the case to attach.
   */
  initialCaseNumber?: string;
}

const CaseSearch: React.FC<CaseSearchProps> = ({ brand, county, onCaseSelected, anchorId, initialCaseNumber }) => {
  const { fetchWithAuth } = useApi();
  // R105: seed arrives from the extraction-stashed number (CaseAttachPanel gates
  // it on `confirmFlowEnabled && !caseTrackingId`). The initializer seeds the
  // input at mount; the re-seed effect below covers a stash that lands after.
  const [searchQuery, setSearchQuery] = useState(initialCaseNumber ?? '');
  const [searchType, setSearchType] = useState<'name' | 'caseNumber'>('caseNumber');
  const [results, setResults] = useState<CaseSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCase, setSelectedCase] = useState<CaseDetail | null>(null);
  const [loadingCase, setLoadingCase] = useState(false);

  // Re-seed when the stash arrives AFTER mount — an extraction that runs once
  // the panel is on screen flips `initialCaseNumber` from '' to the stashed
  // number. The `!searchQuery` guard is the entire "never overwrite a filer-
  // entered value" guarantee (R4): once the filer has typed, this is a no-op.
  // The write makes `searchQuery` non-empty, so the effect self-terminates and
  // never loops. The input keeps `value={searchQuery}` (component-local), so
  // seeding never converts it to a controlled input bound to wizard state (R5).
  useEffect(() => {
    if (initialCaseNumber && !searchQuery) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- R105 re-seed: sync the stashed case number into component-local searchQuery when it arrives after mount and the input is empty (never overwrites a typed value); the write makes searchQuery non-empty so the effect self-terminates
      setSearchQuery(initialCaseNumber);
    }
  }, [initialCaseNumber, searchQuery]);

  // GetCase returns one party record per subcase, so a multi-subcase case lists a
  // logical party N times. Collapse to the canonical roster (the same
  // referenceId-first dedup the wizard applies at hydration) so the preview count
  // and list match the roster handed downstream.
  const dedupedParties = useMemo(
    () => (selectedCase ? dedupePartyRoster(selectedCase.parties) : []),
    [selectedCase],
  );

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim() || !county) return;

    setLoading(true);
    setError('');
    setResults([]);
    setSelectedCase(null);

    try {
      const param = searchType === 'caseNumber'
        ? `caseNumber=${encodeURIComponent(searchQuery.trim())}`
        : `name=${encodeURIComponent(searchQuery.trim())}`;

      const res = await fetchWithAuth(
        `${API_BASE}/api/efiling/cases/search?county=${encodeURIComponent(county)}&${param}`,
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Search failed' }));
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }

      const json = await res.json();
      setResults(json.data || []);

      if ((json.data || []).length === 0) {
        setError('No cases found. Try a different search term.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchType, county, fetchWithAuth]);

  const handleSelectCase = useCallback(async (caseSummary: CaseSummary) => {
    setLoadingCase(true);
    setError('');

    try {
      const id = caseSummary.trackingId || caseSummary.docketId;
      const byDocket = !caseSummary.trackingId;
      const endpoint = byDocket
        ? `${API_BASE}/api/efiling/cases/docket/${encodeURIComponent(id)}`
        : `${API_BASE}/api/efiling/cases/${encodeURIComponent(id)}`;

      const res = await fetchWithAuth(`${endpoint}?county=${encodeURIComponent(county)}`);

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Failed to load case' }));
        throw new Error(body.message || body.error || `HTTP ${res.status}`);
      }

      const json = await res.json();
      const caseDetail = json.data as CaseDetail;
      setSelectedCase(caseDetail);
      onCaseSelected(caseDetail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load case details');
    } finally {
      setLoadingCase(false);
    }
  }, [county, fetchWithAuth, onCaseSelected]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="mb-6" id={anchorId} data-testid="case-search">
      <h3
        className="text-sm font-semibold mb-3"
        style={{ color: brand.colors.text }}
      >
        Search Existing Case
      </h3>

      {/* Search type toggle */}
      <ChoiceGroup question="Search by" hideLegend className="flex gap-4 mb-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="caseSearchType"
            checked={searchType === 'caseNumber'}
            onChange={() => setSearchType('caseNumber')}
            style={{ accentColor: brand.colors.primary }}
          />
          <span style={{ color: brand.colors.text }}>Case Number</span>
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="radio"
            name="caseSearchType"
            checked={searchType === 'name'}
            onChange={() => setSearchType('name')}
            style={{ accentColor: brand.colors.primary }}
          />
          <span style={{ color: brand.colors.text }}>Party Name</span>
        </label>
      </ChoiceGroup>

      {/* Search input */}
      <div className="flex gap-2">
        <div className="flex-1">
          {/* SPEC-SOR-321-v1 R1: the placeholder is a hint, never the name —
              the visually-hidden label is what assistive tech announces. One
              wizard tab mounts at a time, so the fixed id cannot collide. */}
          <label htmlFor="case-search-query" className="sr-only">
            {searchType === 'caseNumber' ? 'Search by case number' : 'Search by party last name'}
          </label>
          <input
            id="case-search-query"
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchType === 'caseNumber' ? 'Enter case number...' : 'Enter party last name...'}
            className="w-full px-3 py-2 border rounded-lg text-sm"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={loading || !searchQuery.trim()}
          className="px-4 py-2 rounded-lg text-sm font-medium self-start mt-1"
          style={{
            backgroundColor: loading || !searchQuery.trim() ? brand.colors.border : brand.colors.primary,
            color: loading || !searchQuery.trim() ? brand.colors.textMuted : brand.colors.onPrimary,
            cursor: loading || !searchQuery.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm mt-2" style={{ color: '#EF4444' }}>
          {error}
        </p>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="mt-3 border rounded-lg overflow-hidden" style={{ borderColor: brand.colors.border }}>
          <div
            className="text-xs font-semibold uppercase px-3 py-2"
            style={{ color: brand.colors.textLight, backgroundColor: '#F9FAFB', borderBottom: `1px solid ${brand.colors.border}` }}
          >
            {results.length} case{results.length !== 1 ? 's' : ''} found
          </div>
          {results.map(c => (
            <button
              key={c.trackingId || c.docketId}
              onClick={() => handleSelectCase(c)}
              disabled={loadingCase}
              className="w-full text-left px-3 py-2 text-sm hover:opacity-80 transition-colors"
              style={{
                color: brand.colors.text,
                borderBottom: `1px solid ${brand.colors.border}`,
                backgroundColor: selectedCase?.trackingId === c.trackingId ? `${brand.colors.primary}10` : 'transparent',
              }}
            >
              <span className="font-medium">{c.docketId || c.trackingId}</span>
              {c.title && <span className="ml-2">{c.title}</span>}
              {c.category && (
                <span className="ml-2 text-xs" style={{ color: brand.colors.textLight }}>
                  ({c.category})
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Selected case details */}
      {selectedCase && (
        <div
          className="mt-3 p-3 rounded-lg border"
          style={{ borderColor: brand.colors.primary, backgroundColor: `${brand.colors.primary}05` }}
        >
          <p className="text-sm font-medium" style={{ color: brand.colors.link }}>
            Selected: {selectedCase.docketId || selectedCase.trackingId}
          </p>
          <p className="text-sm" style={{ color: brand.colors.text }}>
            {selectedCase.title}
          </p>
          {dedupedParties.length > 0 && (
            <p className="text-xs mt-1" data-testid="selected-case-parties" style={{ color: brand.colors.textLight }}>
              {dedupedParties.length} parties: {dedupedParties.map(p =>
                `${p.role}: ${p.isOrganization ? p.organizationName : `${p.firstName} ${p.lastName}`}`.trim()
              ).join(', ')}
            </p>
          )}
          {selectedCase.subcases.length > 0 && (
            <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
              {selectedCase.subcases.length} subcase(s)
            </p>
          )}
        </div>
      )}

      {loadingCase && (
        <p className="text-sm mt-2" style={{ color: brand.colors.textLight }}>
          Loading case details...
        </p>
      )}
    </div>
  );
};

export default CaseSearch;
